import { Component, ComponentInterface, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import {
    clamp,
    isValidNumber,
    isValidNumericKeyboardEvent,
    parseStringToStringNumber,
    sanitizeNumericInput,
    toMaxPrecision
} from "../../utils/number-utils/numbers";
import { Direction, FormatFn, ParseFn } from "./interface";

@Component({
    tag: "ux-input-field-number",
    styleUrl: "input-field-number.scss"
})
export class InputFieldNumber implements ComponentInterface {
    private isTyping = false;
    private inputFieldElement: HTMLUxInputFieldElement;

    @State() inputValue: string;

    /** Label text */
    @Prop() readonly label: string = "";
    /** The name attribute of the input element */
    @Prop() readonly name: string = "";
    /** Placeholder for the input element */
    @Prop() readonly placeholder: string = "";
    /** If set to `true`, clamps the value to `min` and `max` after losing focus.
    @Prop() clampOnBlur: boolean = false;
     */
    /** The default value of the input */
    @Prop() readonly defaultValue?: number;
    /** Indicates if the input is disabled */
    @Prop() readonly disabled: boolean = false;
    /** Indicates if the input is required  */
    @Prop() readonly required: boolean = false;
    /** Indicates if the input value is invalid */
    @Prop({ mutable: true }) error = false;
    /** Optional error message that is rendered if `error` is set to `true` */
    @Prop() readonly errorMsg: string = "";
    /** Additional hint text shown below the input */
    @Prop() readonly hintText: string = "";
    /** An optional unit is shown besides the input */
    @Prop() readonly unit: string = "";
    /** The locale to use for input validation. */
    @Prop() readonly locale?: string;
    /** The minimum value. */
    @Prop() readonly min?: number;
    /** The maximum value. */
    @Prop() readonly max?: number;
    /** The maximum number of decimal places. */
    @Prop() readonly precision: number = 0;
    /** The increment size to use when using keyboard navigation. */
    @Prop() readonly step: number = 1;
    /** A valid number or `Number.NaN`. */
    @Prop({ mutable: true }) value: number;
    /** A function that returns a string representation of a given number. */
    @Prop() readonly formatter: FormatFn;
    /** A function that parses a number from a given string. */
    @Prop() readonly parser: ParseFn;

    /** Emitted when the value changes. */
    @Event() uxChange!: EventEmitter;

    /** Emitted when the input is invalid. */
    @Event() uxInvalid!: EventEmitter;

    @Watch("value")
    updateInputValue() {
        let formatted: string | null = null;
        if (typeof this.formatter === "function") {
            formatted = this.formatter(this.value, this.isTyping);
        }
        if (formatted == null) {
            formatted = this.defaultFormatter();
        }
        this.inputValue = formatted;
    }

    /** Returns the native input element */
    @Method()
    async getInputElement(): Promise<HTMLInputElement> {
        return this.inputFieldElement.getInputElement();
    }

    /** Set focus on the native input element */
    @Method()
    setFocus(): Promise<void> {
        return this.inputFieldElement.setFocus();
    }

    /** Validate the input value and set `error` accordingly. */
    @Method()
    async validate() {
        this.error =
            !isValidNumber(this.value) ||
            (this.min != null && this.min > this.value) ||
            (this.max != null && this.max < this.value);
        if (this.error) {
            this.uxInvalid.emit();
        }
    }

    connectedCallback() {
        // @ts-expect-error Check for typing
        this.value = isValidNumber(this.value) ? this.value : this.defaultValue;
        this.updateInputValue();
    }

    private defaultFormatter() {
        if (isValidNumber(this.value)) {
            return this.value.toLocaleString(this.locale, {
                maximumFractionDigits: this.isTyping ? undefined : this.precision
            });
        } else {
            return this.inputValue;
        }
    }

    private defaultParser() {
        const numString = parseStringToStringNumber(sanitizeNumericInput(this.inputValue, this.locale), this.locale);
        return numString === "" ? Number.NaN : Number(numString);
    }

    private handleNextValue(value: number, isTyping = false) {
        this.isTyping = isTyping;
        if (this.value !== value) {
            this.value = value;
            this.uxChange.emit();
        }
    }

    private getStepDelta(direction: Direction) {
        return direction * this.step;
    }

    private incrementValue(delta: number) {
        const currentValue = isValidNumber(this.value) ? this.value : isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        // @ts-expect-error Check for typing
        let nextValue = toMaxPrecision(currentValue + delta, this.precision);
        nextValue = clamp(nextValue, this.min, this.max);
        this.handleNextValue(nextValue);
    }

    private handleKeydown = (event: KeyboardEvent) => {
        const { key } = event;
        if (key === "ArrowUp") {
            this.incrementValue(this.getStepDelta(Direction.UP));
        } else if (key === "ArrowDown") {
            this.incrementValue(this.getStepDelta(Direction.DOWN));
        } else if (key === "Home" && this.min != null) {
            this.handleNextValue(this.min);
        } else if (key === "End" && this.max != null) {
            this.handleNextValue(this.max);
        } else if (key === "Enter") {
            this.handleBlur();
        }
    };

    private handleKeypress = (event: KeyboardEvent) => {
        if (!isValidNumericKeyboardEvent(event, this.locale)) {
            event.preventDefault();
        }
    };

    private handlePaste = () => {
        return;
    };

    private handleInput = () => {
        this.inputValue = this.inputFieldElement.value;
        const num = typeof this.parser === "function" ? this.parser(this.inputValue) : this.defaultParser();
        // @ts-expect-error Check for typing
        this.handleNextValue(num, true);
    };

    private handleChange = (e: CustomEvent) => {
        e.stopPropagation();
    };

    private handleBlur = () => {
        let nextValue = this.value;
        if (isValidNumber(nextValue)) {
            nextValue = toMaxPrecision(this.value, this.precision);
        } else {
            // @ts-expect-error Check for typing
            nextValue = isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        }
        if (isValidNumber(this.min) || isValidNumber(this.max)) {
            nextValue = clamp(nextValue, this.min, this.max);
        }
        this.handleNextValue(nextValue);
    };

    render() {
        const { handleKeydown, handleKeypress, handleChange, handleInput, handlePaste, handleBlur } = this;
        return (
            <Host>
                <ux-input-field
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.inputFieldElement = el)}
                    type="text"
                    inputmode="decimal"
                    disabled={this.disabled}
                    label={this.label}
                    hintText={this.hintText}
                    error={this.error}
                    errorMsg={this.errorMsg}
                    placeholder={this.placeholder}
                    unit={this.unit}
                    value={this.inputValue}
                    required={this.required}
                    onKeyDown={handleKeydown}
                    onKeyPress={handleKeypress}
                    onPaste={handlePaste}
                    onUxChange={handleChange}
                    onUxInput={handleInput}
                    onUxBlur={handleBlur}
                >
                    <div class="ux-input-field-number__buttons" slot="buttons">
                        <slot name="buttons" />
                    </div>
                </ux-input-field>
                {this.name && <input type="hidden" name={this.name} value={this.value} />}
            </Host>
        );
    }
}
